import { FC, useCallback, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getStaffDetailPath, getStaffReturnPath, getStaffSection } from '../../../../app/router/staffNavigation';
import { useAppSelector } from "../../../../app/store/store";
import { FEATURES, hasFeature } from "../../../../entities/my";
import { idmService } from "../../../../entities/idm";
import { IStaffProfileSectionsContext } from "../../model/types";
import { getStaffPermissions } from "../../model/getStaffPermissions";
import { useStaffFeedback } from "../../model/useStaffFeedback";
import { useStaffMember } from "../../model/useStaffMember";
import { StaffDetailLayout } from "../staffDetail/StaffDetailLayout";
import { StaffProfileContent } from "../staffProfile/StaffProfileContent";
import { StaffProfileModals } from "../staffProfile/StaffProfileModals";
import { useStaffProfileViewModel } from "../staffProfile/useStaffProfileViewModel";
import { IStaffCardWidgetProps } from "../../model/types";

export const StaffCardWidget: FC<IStaffCardWidgetProps> = ({renderProfileSections}) => {
    const navigate = useNavigate()
    const {pathname, search} = useLocation()
    const section = getStaffSection(pathname)
    const backPath = getStaffReturnPath(search, section)
    const {user_id} = useParams<{user_id: string}>()
    const staffId = user_id && !Number.isNaN(Number(user_id)) ? Number(user_id) : null
    const {my} = useAppSelector(state => state.myReducer)
    const permissions = getStaffPermissions(my, staffId || undefined)
    const canReadScheduleHistory = Boolean(staffId) && hasFeature(my, FEATURES.AUDIT.SCHEDULE_HISTORY_READ, staffId || undefined)
    const {handleError, showError, showSuccess} = useStaffFeedback()
    const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false)
    const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false)
    const [isResetPasswordLoading, setIsResetPasswordLoading] = useState(false)
    const [isDeleteUserLoading, setIsDeleteUserLoading] = useState(false)
    const [isTutorStudentsMove, setIsTutorStudentsMove] = useState(false)

    const onNotFound = useCallback(() => {
        navigate(backPath, {replace: true})
        showError('Сотрудник не найден')
    }, [backPath, navigate, showError])

    const {
        isLoading,
        loadError,
        loadStaffMember,
        staffMember,
        userPersonalData,
    } = useStaffMember({staffId, onError: handleError, onNotFound})

    const {
        assistantProfile,
        isAssistantStaff,
        isHrStaff,
        isShiftStaff,
        isTutorProfileLoading,
        isTutorProfileResolved,
        isTutorStaff,
        tutorDataList,
        tutorHeaderData,
        onTutorArchiveChange,
        onTutorSanctionSequenceReset,
        onTutorTagsChange,
        tutorLegacyId,
        tutorUserId,
    } = useStaffProfileViewModel({
        selectedStaffId: staffId,
        staffMember,
        userPersonalData,
    })

    const editPath = staffId
        ? getStaffDetailPath(staffId, section, search, '/update')
        : backPath

    const onResetPassword = async () => {
        if(!staffId){
            return
        }

        try{
            setIsResetPasswordLoading(true)
            await idmService.resetUserPassword(staffId)
            setResetPasswordModalOpen(false)
            showSuccess(`Пароль сотрудника ${userPersonalData?.full_name || staffMember.full_name} успешно сброшен`)
        }
        catch(error){
            handleError(error, 'Ошибка при сбросе пароля сотрудника')
        }
        finally{
            setIsResetPasswordLoading(false)
        }
    }

    const onDeleteUser = async () => {
        if(!staffId){
            return
        }

        try{
            setIsDeleteUserLoading(true)
            await idmService.deleteUser(staffId)
            setDeleteUserModalOpen(false)
            navigate(backPath, {replace: true})
            showSuccess(`Сотрудник ${userPersonalData?.full_name || staffMember.full_name} удален`)
        }
        catch(error){
            handleError(error, 'Ошибка при удалении сотрудника')
        }
        finally{
            setIsDeleteUserLoading(false)
        }
    }

    const profileSectionsContext: IStaffProfileSectionsContext | null = staffMember.user_id > 0
        ? {
            staffUserId: staffMember.user_id,
            assistantProfile,
            showAssistantProfile: isAssistantStaff && Boolean(assistantProfile),
            showAssistantPenaltyBonus: (isAssistantStaff || isShiftStaff || isHrStaff)
                && (permissions.canAssistantPenaltyRead || permissions.canAssistantPenaltyCreate),
            showTutorProfile: isTutorStaff,
            isTutorProfileLoading: isTutorProfileLoading || !isTutorProfileResolved,
            tutorUserId,
            tutorLegacyId: tutorLegacyId > 0 ? tutorLegacyId : undefined,
            tutorHeaderData,
            onTutorArchiveChange,
            onTutorSanctionSequenceReset,
            onTutorTagsChange,
            onWorkTgMoveSuccess: loadStaffMember,
            tutorDataList,
            isTutorStudentsMove,
            setIsTutorStudentsMove,
            editPath,
            canResetStaffPassword: permissions.canResetStaffPassword,
            onResetStaffPassword: () => setResetPasswordModalOpen(true),
            canMoveStaffWorkTg: permissions.canMoveStaffWorkTg,
            showTutorFinance: permissions.canTutorFinanceRead,
            showTutorPayouts: permissions.canTutorPayouts,
            showTutorPenaltyBonus: permissions.canTutorPenaltyBonus,
            showTutorStudents: permissions.canTutorStudentsView,
            showShiftCalendar: isShiftStaff,
        }
        : null

    const isContentLoading = Boolean(staffId) && !loadError && (isLoading || staffMember.user_id !== staffId)

    return (
        <StaffDetailLayout
            staffId={staffId}
            isLoading={isContentLoading}
            loadError={loadError}
            selectedTab={1}
            showSwitch={permissions.canReadIdmStaff}
            showPaymentDetailsTab={isTutorStaff}
            showHistoryTab={isTutorStaff && canReadScheduleHistory}
        >
            <StaffProfileContent
                staffMember={staffMember}
                userPersonalData={userPersonalData}
                isTutorStaff={isTutorStaff}
                assistantProfile={assistantProfile}
                canEditStaff={permissions.canEditStaff}
                canResetStaffPassword={permissions.canResetStaffPassword}
                canDeleteStaff={permissions.canDeleteStaff}
                canArchiveStaff={permissions.canArchiveStaff}
                canUnarchiveStaff={permissions.canUnarchiveStaff}
                canMoveStaffWorkTg={permissions.canMoveStaffWorkTg}
                onEdit={() => navigate(editPath)}
                onResetPassword={() => setResetPasswordModalOpen(true)}
                onDelete={() => setDeleteUserModalOpen(true)}
                onArchiveSuccess={loadStaffMember}
                onWorkTgMoveSuccess={loadStaffMember}
                profileSectionsContext={profileSectionsContext}
                renderProfileSections={renderProfileSections}
            />
            <StaffProfileModals
                staffName={userPersonalData?.full_name || staffMember.full_name}
                resetPasswordModalOpen={resetPasswordModalOpen}
                setResetPasswordModalOpen={setResetPasswordModalOpen}
                deleteUserModalOpen={deleteUserModalOpen}
                setDeleteUserModalOpen={setDeleteUserModalOpen}
                onResetPassword={onResetPassword}
                onDeleteUser={onDeleteUser}
                isResetPasswordLoading={isResetPasswordLoading}
                isDeleteUserLoading={isDeleteUserLoading}
            />
        </StaffDetailLayout>
    )
}
